import Route from '@ember/routing/route'
import { service } from '@ember/service'
import type { IntlService } from 'ember-intl'
import type ReportingService from 'teacher-ui-client/services/reporting'
import { restartableTask, timeout } from 'ember-concurrency'
import { Product } from 'teacher-ui-client/utils/products'
import { toRequestScopeStudent } from 'teacher-ui-client/services/reporting'
import type {
  RequestScope,
  ReportingQueryPage,
  ReportingQuerySort,
  ReportingQueryFilter,
} from 'teacher-ui-client/services/reporting'
import type ReportingUiScopeRoute from 'teacher-ui-client/reporting/ui-scope/route'
import type ReadingEggspressDashboardStudentRoute from 'teacher-ui-client/reporting/ui-scope/rex/dashboard/student/route'
import type {
  ComprehensionProgressOverviewReportData,
  ComprehensionProgressEventsReportData,
  ComprehensionProgressLessonEvent,
} from 'teacher-ui-client/reporting/ui-scope/rex/dashboard/student/index/route'
import { ExportType } from 'teacher-ui-client/components/reporting/export-select'
import type FileDownload from 'teacher-ui-client/services/file-download'
import { formatExcel } from 'teacher-ui-client/utils/dates'
import { reportingFilename } from 'teacher-ui-client/utils/filename'
import type { Params as ComprehensionProgressIndexParams } from 'teacher-ui-client/reporting/ui-scope/rex/dashboard/student/comprehension-lesson-progress/index/route'
import type { NormalizedInterval } from 'date-fns'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'

export default class extends Route {
  @service reporting!: ReportingService

  @service intl!: IntlService

  @service fileDownload!: FileDownload

  @service flashQueue!: FlashQueueService

  buildRouteInfoMetadata() {
    return {
      pageTitle: this.intl.t('pages.reporting.readingEggspress.dashboard.student.comprehensionLessons.title'),
      exportOptions: [
        {
          exportType: ExportType.CSV,
          exportAction: () => this.exportCsv(),
        },
        { exportType: ExportType.print },
      ],
    }
  }

  model() {
    const { period } = this.modelFor('reporting.ui-scope') as ModelFor<ReportingUiScopeRoute>

    const { student } = this.modelFor(
      'reporting.ui-scope.rex.dashboard.student',
    ) as ModelFor<ReadingEggspressDashboardStudentRoute>
    const requestScope = toRequestScopeStudent(student.id)

    void this.fetchComprehensionProgressOverviewReportTask.perform(Product.REX, period, requestScope).catch(() => {})

    void this.fetchComprehensionProgressPlacementEventsReportTask
      .perform(Product.REX, 'created_at', { number: 1, size: 5 }, { placement: true }, period, requestScope)
      .catch(() => {})

    // performing the events task is left up to the sub-route, where it has page/sort query params.
    // this is done in a sub-route to ensure the tasks in this model are not recalled when those QPs change.

    return {
      period,
      requestScope,
      fetchComprehensionProgressOverviewReportTask: this.fetchComprehensionProgressOverviewReportTask,
      fetchComprehensionProgressPlacementEventsReportTask: this.fetchComprehensionProgressPlacementEventsReportTask,
      fetchComprehensionProgressEventsReportTask: this.fetchComprehensionProgressEventsReportTask,
    }
  }

  fetchComprehensionProgressOverviewReportTask = restartableTask(
    async (...args: Parameters<typeof this.fetchComprehensionProgressOverviewReport>) => {
      await timeout(200)
      return this.fetchComprehensionProgressOverviewReport(...args)
    },
  )

  fetchComprehensionProgressPlacementEventsReportTask = restartableTask(
    async (...args: Parameters<typeof this.fetchComprehensionProgressEventsReport>) => {
      await timeout(200)
      return this.fetchComprehensionProgressEventsReport(...args)
    },
  )

  fetchComprehensionProgressEventsReportTask = restartableTask(
    async (...args: Parameters<typeof this.fetchComprehensionProgressEventsReport>) => {
      await timeout(200)
      return this.fetchComprehensionProgressEventsReport(...args)
    },
  )

  fetchComprehensionProgressOverviewReport(product: Product, period: NormalizedInterval, requestScope: RequestScope) {
    const reportName = 'student-dashboard/comprehension-lesson-progress/overview'

    const url = this.reporting.buildGravityUrl({ product, requestScope, reportName })

    const query = this.reporting.buildQuery({ period })

    return this.reporting.fetchReport<ComprehensionProgressOverviewReportData>(url, query)
  }

  fetchComprehensionProgressEventsReport(
    product: Product,
    sort: ReportingQuerySort,
    page: ReportingQueryPage,
    filter: ReportingQueryFilter,
    period: NormalizedInterval,
    requestScope: RequestScope,
  ) {
    const reportName = 'student-dashboard/comprehension-lesson-progress/events'

    const url = this.reporting.buildGravityUrl({ product, requestScope, reportName })

    const query = this.reporting.buildQuery({ sort, page, filter, period })

    return this.reporting.fetchReport<ComprehensionProgressEventsReportData>(url, query)
  }

  async exportCsv() {
    const { period } = this.modelFor('reporting.ui-scope') as ModelFor<ReportingUiScopeRoute>
    const { student } = this.modelFor(
      'reporting.ui-scope.rex.dashboard.student',
    ) as ModelFor<ReadingEggspressDashboardStudentRoute>
    const requestScope = toRequestScopeStudent(student.id)
    const { sort } = this.paramsFor(
      'reporting.ui-scope.rex.dashboard.student.comprehension-lesson-progress.index',
    ) as ComprehensionProgressIndexParams
    const page = 'all'

    let overviewReport: Awaited<ReturnType<typeof this.fetchComprehensionProgressOverviewReport>>
    let placementEventsReport: Awaited<ReturnType<typeof this.fetchComprehensionProgressEventsReport>>
    let eventsReport: Awaited<ReturnType<typeof this.fetchComprehensionProgressEventsReport>>
    try {
      overviewReport = await this.fetchComprehensionProgressOverviewReport(Product.REX, period, requestScope)
      placementEventsReport = await this.fetchComprehensionProgressEventsReport(
        Product.REX,
        sort,
        page,
        { placement: true },
        period,
        requestScope,
      )
      eventsReport = await this.fetchComprehensionProgressEventsReport(
        Product.REX,
        sort,
        page,
        { placement: false },
        period,
        requestScope,
      )
    } catch {
      this.flashQueue.addFail({ subtitle: this.intl.t('failedToExportReport') })
      return
    }

    const overviewHeader = [
      this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.initialLesson'),
      this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.highestLessonCompleted'),
      this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.totalLessonsCompleted'),
      this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.numberOfQuizzesTaken'),
      `${this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.averageQuizScore')} %`,
      this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.totalAttempts'),
    ]
    const overviewRow = [
      overviewReport.overview.initial_lesson,
      overviewReport.overview.highest_lesson_completed,
      overviewReport.overview.total_lessons_completed,
      overviewReport.overview.number_of_quizzes_taken,
      overviewReport.overview.average_quiz_score,
      overviewReport.overview.total_attempts,
    ]

    const placementHeader = [
      this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.date'),
      this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.lesson'),
      this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.lessonName'),
    ]
    const placementRows = placementEventsReport.events.map((ev) => [
      formatExcel(new Date(ev.created_at)),
      ev.lesson,
      ev.lesson_name,
    ])

    const eventHeader = [
      this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.date'),
      this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.lesson'),
      this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.lessonName'),
      `${this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.score')} %`,
      this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.numberOfAttempts'),
    ]
    // report was run with the filter of placement: false, so we can assume these are all lesson events.
    const eventRows = (eventsReport.events as ComprehensionProgressLessonEvent[]).map((ev) => [
      formatExcel(new Date(ev.created_at)),
      ev.lesson,
      ev.lesson_name,
      ev.score,
      ev.number_of_attempts,
    ])

    const overviewFilename = `${reportingFilename('comprehension-lesson-overview', student.fullName, period)}.csv`
    const placementsFilename = `${reportingFilename('comprehension-lesson-placements', student.fullName, period)}.csv`
    const eventsFilename = `${reportingFilename('comprehension-lesson-recent-activity', student.fullName, period)}.csv`

    this.fileDownload.downloadAsCsv([overviewHeader, overviewRow], { filename: overviewFilename, header: true })
    this.fileDownload.downloadAsCsv([placementHeader, ...placementRows], { filename: placementsFilename, header: true })
    this.fileDownload.downloadAsCsv([eventHeader, ...eventRows], { filename: eventsFilename, header: true })
  }
}
